import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Chip,
    Box,
    Paper,
    Divider
} from '@mui/material';
import CustomDatePicker from '../Calendar/CustomDatePicker';
import api, { fetchProductById } from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { Product } from '../../types';

interface Customer {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    level: string;
}

interface TimeSlot {
    _id: string;
    date: string;
    time: string;
    startTime: string;
    capacity: number;
    isAvailable: boolean;
    remainingCapacity?: number;
    totalCapacity?: number;
    reserved?: number;
}

interface SelectedSession {
    date: Date;
    dateStr: string;
    timeSlot: TimeSlot;
}

interface FormData {
    date: Date | null;
    timeSlot: TimeSlot | null;
    participants: number;
    customer: Customer;
}

const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé'];

const ReservationForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { addItem, clearCart } = useCart();
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
    const [noSlotsAvailable, setNoSlotsAvailable] = useState(false);
    const [selectedSessions, setSelectedSessions] = useState<SelectedSession[]>([]);
    const [formData, setFormData] = useState<FormData>({
        date: null,
        timeSlot: null,
        participants: 1,
        customer: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            level: 'Débutant'
        }
    });
    const navigate = useNavigate();

    const numberOfSessions = product?.numberOfSessions || 1;
    const isMultiSession = numberOfSessions > 1;

    useEffect(() => {
        const loadProduct = async () => {
            if (id) {
                try {
                    const data = await fetchProductById(id);
                    setProduct(data);
                } catch (err) {
                    setError('Erreur lors du chargement du produit');
                }
            }
        };
        loadProduct();
    }, [id]);

    const fetchAvailableSlots = async (date: Date) => {
        try {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;

            const response = await api.get('/planning/slots', {
                params: { date: formattedDate, productId: id }
            });
            const slots = response.data;

            setAvailableSlots(slots);
            setNoSlotsAvailable(slots.length === 0);
        } catch (err) {
            setError('Erreur lors de la récupération des créneaux disponibles');
            setAvailableSlots([]);
            setNoSlotsAvailable(true);
        }
    };

    const handleDateChange = (date: Date | null) => {
        setFormData(prev => ({ ...prev, date, timeSlot: null }));
        if (date && !isNaN(date.getTime()) && date.getFullYear() >= 2020) {
            fetchAvailableSlots(date);
        } else {
            setAvailableSlots([]);
            setNoSlotsAvailable(false);
        }
    };

    const formatDateStr = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSlotSelect = (slot: TimeSlot) => {
        if (isMultiSession && formData.date) {
            const dateStr = formatDateStr(formData.date);
            const alreadySelected = selectedSessions.some(
                s => s.dateStr === dateStr && s.timeSlot._id === slot._id
            );

            if (alreadySelected) {
                setSelectedSessions(prev => 
                    prev.filter(s => !(s.dateStr === dateStr && s.timeSlot._id === slot._id))
                );
            } else if (selectedSessions.length < numberOfSessions) {
                setSelectedSessions(prev => [
                    ...prev,
                    { date: formData.date!, dateStr, timeSlot: { ...slot, startTime: slot.time } }
                ]);
            } else {
                setError(`Vous avez déjà sélectionné ${numberOfSessions} sessions`);
            }
        } else {
            setFormData(prev => ({
                ...prev, timeSlot: {
                    ...slot,
                    startTime: slot.time
                }
            }));
        }
    };

    const handleRemoveSession = (index: number) => {
        setSelectedSessions(prev => prev.filter((_, i) => i !== index));
    };

    const handleCustomerChange = (field: keyof Customer, value: string) => {
        setFormData(prev => ({
            ...prev,
            customer: { ...prev.customer, [field]: value }
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!formData.customer.firstName || !formData.customer.lastName || 
                !formData.customer.email || !formData.customer.phone) {
                setError('Veuillez remplir tous les champs obligatoires');
                setLoading(false);
                return;
            }

            if (isMultiSession) {
                if (selectedSessions.length < numberOfSessions) {
                    setError(`Veuillez sélectionner ${numberOfSessions} sessions (${selectedSessions.length}/${numberOfSessions} sélectionnées)`);
                    setLoading(false);
                    return;
                }

                clearCart();
                if (product) {
                    addItem({
                        id: product._id,
                        name: `${product.name} (${numberOfSessions} plongées)`,
                        description: product.description,
                        price: product.price,
                        imageUrl: product.imageUrl
                    });
                }

                const multiSessions = selectedSessions.map(session => {
                    let endTime = session.timeSlot.time;
                    if (product?.details?.duration) {
                        const durationMatch = product.details.duration.match(/(\d+)/);
                        if (durationMatch) {
                            const durationHours = parseInt(durationMatch[1]);
                            const [hours, minutes] = session.timeSlot.time.split(':').map(Number);
                            const endHours = hours + durationHours;
                            endTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                        }
                    }

                    return {
                        date: session.dateStr,
                        timeSlot: {
                            ...session.timeSlot,
                            endTime
                        },
                        participants: 1,
                        customer: formData.customer,
                        product: product
                    };
                });

                navigate('/payment', {
                    state: {
                        multiSessions,
                        isMultiSession: true,
                        reservation: multiSessions[0]
                    }
                });
            } else {
                if (!formData.date || !formData.timeSlot) {
                    setError('Veuillez sélectionner une date et un créneau');
                    setLoading(false);
                    return;
                }

                const availableCapacity = formData.timeSlot.remainingCapacity !== undefined
                    ? formData.timeSlot.remainingCapacity
                    : formData.timeSlot.capacity;

                if (formData.participants > availableCapacity) {
                    setError(`Il ne reste que ${availableCapacity} place(s) disponible(s) pour ce créneau`);
                    setLoading(false);
                    return;
                }

                clearCart();
                if (product) {
                    addItem({
                        id: product._id,
                        name: `${product.name} (${formData.participants} participant${formData.participants > 1 ? 's' : ''})`,
                        description: product.description,
                        price: product.price * formData.participants,
                        imageUrl: product.imageUrl
                    });
                }

                const formattedDate = formatDateStr(formData.date);

                let endTime = formData.timeSlot.time;
                if (product?.details?.duration) {
                    const durationMatch = product.details.duration.match(/(\d+)/);
                    if (durationMatch) {
                        const durationHours = parseInt(durationMatch[1]);
                        const [hours, minutes] = formData.timeSlot.time.split(':').map(Number);
                        const endHours = hours + durationHours;
                        endTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                    }
                }

                navigate('/payment', {
                    state: {
                        reservation: {
                            date: formattedDate,
                            timeSlot: {
                                ...formData.timeSlot,
                                endTime
                            },
                            participants: formData.participants,
                            customer: formData.customer,
                            product: product
                        }
                    }
                });
            }
        } catch (err) {
            setError('Erreur lors de la soumission de la réservation');
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = () => {
        const customerValid = formData.customer.firstName &&
            formData.customer.lastName &&
            formData.customer.email &&
            formData.customer.phone;

        if (isMultiSession) {
            return customerValid && selectedSessions.length === numberOfSessions;
        }
        return customerValid && formData.date && formData.timeSlot;
    };

    const isSlotSelected = (slot: TimeSlot): boolean => {
        if (!isMultiSession) {
            return formData.timeSlot?._id === slot._id;
        }
        if (!formData.date) return false;
        const dateStr = formatDateStr(formData.date);
        return selectedSessions.some(s => s.dateStr === dateStr && s.timeSlot._id === slot._id);
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Produit"
                        value={product ? product.name : ''}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        sx={{ mb: 3 }}
                    />
                </Grid>

                {isMultiSession && (
                    <Grid item xs={12}>
                        <Paper elevation={3} sx={{ p: 2, bgcolor: '#e3f2fd', mb: 2 }}>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Sélectionnez vos {numberOfSessions} plongées
                            </Typography>
                            <Typography variant="body1">
                                Choisir vos dates dans le calendrier puis sélectionnez le créneau ci-dessous. Recommencez pour ajouter une nouvelle date.
                            </Typography>
                            <Alert severity="warning" sx={{ mt: 2, border: '2px solid red' }}>
                                <strong>Important :</strong> Si vous souhaitez modifier vos dates après réservation, contactez-nous afin de vous satisfaire.
                            </Alert>
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                    label={`${selectedSessions.length} / ${numberOfSessions} sélectionnées`}
                                    color={selectedSessions.length === numberOfSessions ? 'success' : 'warning'}
                                    variant="filled"
                                />
                            </Box>
                            {selectedSessions.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Sessions sélectionnées :</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {selectedSessions.map((session, index) => (
                                            <Chip
                                                key={index}
                                                label={`${new Date(session.dateStr).toLocaleDateString('fr-FR')} à ${session.timeSlot.time}`}
                                                onDelete={() => handleRemoveSession(index)}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}

                {error && (
                    <Grid item xs={12}>
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Légende :</strong> Les dates avec un fond vert indiquent qu'il existe des créneaux disponibles.
                            {isMultiSession && ' Cliquez sur plusieurs créneaux pour sélectionner vos plongées.'}
                        </Typography>
                    </Alert>
                </Grid>

                <Grid item xs={12}>
                    <CustomDatePicker
                        label="Date"
                        value={formData.date}
                        onChange={handleDateChange}
                        productId={id}
                        minDate={new Date()}
                        required
                    />
                </Grid>

                {formData.date && (
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            {isMultiSession 
                                ? `Cliquez sur un créneau pour l'ajouter (${selectedSessions.length}/${numberOfSessions})`
                                : 'Cliquer sur le créneau choisi'}
                        </Typography>
                        {noSlotsAvailable ? (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Aucun créneau disponible pour cette date.
                            </Alert>
                        ) : (
                            <Grid container spacing={2}>
                                {availableSlots.map((slot) => (
                                    <Grid item xs={12} sm={6} md={4} key={slot._id}>
                                        <Card
                                            onClick={() => handleSlotSelect(slot)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isSlotSelected(slot) ? 'success.light' : 'background.paper',
                                                border: isSlotSelected(slot) ? '2px solid' : '1px solid',
                                                borderColor: isSlotSelected(slot) ? 'success.main' : 'divider',
                                                '&:hover': {
                                                    bgcolor: isSlotSelected(slot) ? 'success.light' : 'primary.light',
                                                    opacity: 0.9
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Typography variant="h6">{slot.time}</Typography>
                                                <Typography color="textSecondary">
                                                    Places disponibles: {slot.remainingCapacity !== undefined ? slot.remainingCapacity : slot.capacity}
                                                </Typography>
                                                {isSlotSelected(slot) && (
                                                    <Chip label="Sélectionné" color="success" size="small" sx={{ mt: 1 }} />
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                )}

                {!isMultiSession && (
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nombre de participants"
                            type="number"
                            value={formData.participants}
                            onChange={(e) => {
                                const newValue = parseInt(e.target.value);
                                if (formData.timeSlot) {
                                    const availableCapacity = formData.timeSlot.remainingCapacity !== undefined
                                        ? formData.timeSlot.remainingCapacity
                                        : formData.timeSlot.capacity;
                                    if (newValue > availableCapacity) {
                                        setError(`Il ne reste que ${availableCapacity} place(s) disponible(s)`);
                                        setFormData(prev => ({ ...prev, participants: availableCapacity }));
                                        return;
                                    } else {
                                        setError(null);
                                    }
                                }
                                setFormData(prev => ({ ...prev, participants: newValue }));
                            }}
                            inputProps={{
                                min: 1,
                                max: formData.timeSlot ?
                                    (formData.timeSlot.remainingCapacity !== undefined ?
                                        formData.timeSlot.remainingCapacity :
                                        formData.timeSlot.capacity) : 6
                            }}
                            required
                        />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Vos informations</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Prénom"
                        value={formData.customer.firstName}
                        onChange={(e) => handleCustomerChange('firstName', e.target.value)}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Nom"
                        value={formData.customer.lastName}
                        onChange={(e) => handleCustomerChange('lastName', e.target.value)}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.customer.email}
                        onChange={(e) => handleCustomerChange('email', e.target.value)}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Téléphone"
                        value={formData.customer.phone}
                        onChange={(e) => handleCustomerChange('phone', e.target.value)}
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Niveau</InputLabel>
                        <Select
                            value={formData.customer.level}
                            onChange={(e) => handleCustomerChange('level', e.target.value)}
                            label="Niveau"
                            required
                        >
                            {LEVELS.map((level) => (
                                <MenuItem key={level} value={level}>{level}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading || !isFormValid()}
                        size="large"
                    >
                        {loading ? <CircularProgress size={24} /> : 
                            isMultiSession 
                                ? `Réserver mes ${numberOfSessions} plongées` 
                                : 'Réserver'}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default ReservationForm;
